import {useEffect} from 'react';
import {useLatest} from 'react-use';
import {Button, Icon} from '@ezcater/tapas';
import {
  faArrowRightToBracket,
  faBowlHot,
  faBuildingUser,
  faCirclePlus,
  faCog,
  faCreditCard,
  faGift,
  faHeart,
  faHouse,
  faLocationDot,
  faLockOpen,
  faMegaphone,
  faMessageHeart,
  faMoneyCheckDollar,
  faPencil,
  faReceipt,
  faRightFromBracket,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import {CorpAccountDropdown} from '@/components/global/shared/CorpAccountDropdown';
import CorpAccountSelectionMobile from '@/components/global/shared/CorpAccountDropdown/CorpAccountSelectionMobile';
import {EzcaterLogo} from '@/components/ui';
import AppDownloadCTA from '@/components/ui/AppDownloadCTA/AppDownloadCTA';
import EppoFlags from '@/EppoFlags';
import {useBooleanAssignment} from '@/eppoHooks';
import useAuthReturnDestination from '@/hooks/useAuthReturnDestination';
import useCorporateAccount from '@/hooks/useCorporateAccount';
import useEzRewardsEligibility from '@/hooks/useEzRewardsEligibility';
import useIdentity from '@/hooks/useIdentity';
import useOrdererConnectionsCheck from '@/hooks/useOrdererConnectionsCheck';
import usePersonalizedHomePage from '@/hooks/usePersonalizedHomePage';
import useRAFImprovements from '@/hooks/useRAFImprovements';
import useSignOut from '@/hooks/useSignOut';
import useTracking from '@/hooks/useTracking';
import {
  ADDRESSES_PATH,
  ADMIN_PATH,
  CONNECTIONS_PATH,
  CORP_DASHBOARD_PATH,
  EXPENSE_REPORTING_PATH,
  MY_PROFILE_PATH,
  ORDERS_PATH,
  PAYMENT_METHODS_PATH,
  RECEIPTS_PATH,
  REFER_A_FRIEND_PATH,
  REVIEWS_PATH,
  REWARDS_PATH,
  SAVED_CATERERS_PATH,
  SIGN_IN_PATH,
  SIGN_UP_PATH,
} from '@/paths';
import {compilePath} from '@/utils';
import {NAVLINK_TRACKING} from '../appBarTracking';
import {POINTS_GIVEN_FOR_REFER_A_FRIEND, POINTS_GIVEN_FOR_REVIEW} from '../constants';
import {formatPoints} from '../utils';
import MenuLink from './MenuLink';

type UserMenuProps = {
  isMobileMenu?: boolean;
  isLoggedOutHomepage?: boolean;
  closeMenu?: () => void;
};

const UserMenu: React.FC<UserMenuProps> = ({
  isMobileMenu = false,
  isLoggedOutHomepage = false,
  closeMenu = () => {},
}) => {
  const {data: identityData, isAdmin, isLoggedIn} = useIdentity();
  const {landingPagePath} = usePersonalizedHomePage();
  const {isSingleCorporate, corporateDisplayName, corporateLogoIconUrl, hasMultipleAccounts} =
    useCorporateAccount();
  const {canAccessEzRewards, balanceEnabled, earningsEnabled} = useEzRewardsEligibility();
  const {signOut, isSigningOut} = useSignOut();
  const {track, trackClick} = useTracking();
  const {t} = useTranslation('app-bar');
  const {ezRewardsAccount, canAccessConcur, canAccessCorpPortal, fullName} =
    identityData?.me?.consumerAccount || {};
  const {ordererConnectionsEnabled} = useOrdererConnectionsCheck();
  const pendingConnectionsCount =
    identityData?.me?.consumerAccount?.totalPendingUserConnectionInvitations?.totalCount ?? 0;
  const corpAccountUuid = identityData?.me?.consumerAccount?.corporateAccount?.uuid;
  const corpAccountId = identityData?.me?.consumerAccount?.corporateAccount?.id ?? '';
  const shouldHideConnectionsFeatures = useBooleanAssignment(
    EppoFlags.CorporateConnectionsAutoConnect,
    {corpAccountId},
  );
  const firstName = identityData?.me?.consumerAccount?.firstName;

  const {showRAFImprovements} = useRAFImprovements();
  const {returnParams} = useAuthReturnDestination();

  // Track when the user opens the menu
  const trackRef = useLatest(track);
  useEffect(() => {
    trackRef.current?.(NAVLINK_TRACKING.profile.dropdownOpened, {
      misc_json: JSON.stringify({area: isMobileMenu ? 'mobile' : 'desktop'}),
      url: window.location.href,
    });
  }, [isMobileMenu, trackRef]);

  const showCorpAccountSwitcher = isLoggedIn && hasMultipleAccounts;
  // Show the static corporate name/logo only for single corporate accounts when the account switcher
  // is not displayed. For users with multiple accounts, the switcher replaces the static corp name display.
  const showCorpName = corporateDisplayName && isSingleCorporate && !showCorpAccountSwitcher;

  return (
    <div className={twJoin('flex w-full flex-col gap-4 divide-y', isMobileMenu && 'px-4')}>
      {!isLoggedOutHomepage && (
        <div className="block appbar-xl:hidden">
          <div className="font-bold leading-5 text-peppercorn-500">
            {firstName ? t('menus.welcomeFirstName', {firstName}) : t('menus.welcomeToEzCater')}
          </div>
          {showCorpName && (
            <div className="mt-2 flex items-start gap-2 text-peppercorn-500">
              {corporateLogoIconUrl && (
                <Image
                  src={corporateLogoIconUrl}
                  alt={`${corporateDisplayName} logo`}
                  width={24}
                  height={24}
                  className="shrink-0"
                />
              )}
              <span className="break-words">{corporateDisplayName}</span>
            </div>
          )}
        </div>
      )}
      {isLoggedIn ? (
        <>
          {showCorpAccountSwitcher && (
            <>
              {!isMobileMenu && (
                <div
                  className={twJoin(
                    'flex-col items-start',
                    showCorpName ? 'pt-3 appbar-xl:pt-0' : 'mt-2 border-none',
                  )}
                >
                  <CorpAccountDropdown onClose={closeMenu} />
                </div>
              )}
              {isMobileMenu && (
                <div className="border-none">
                  <CorpAccountSelectionMobile />
                </div>
              )}
            </>
          )}
          <div
            className={twJoin(
              'flex flex-col items-start gap-4',
              !showCorpName && !showCorpAccountSwitcher && 'mt-2 border-none',
              (showCorpName || showCorpAccountSwitcher) && 'pt-3',
              showCorpName &&
                !showCorpAccountSwitcher &&
                'desktop:border-t appbar-xl:border-none appbar-xl:pt-0',
            )}
          >
            <MenuLink icon={faHouse} label={t('navLinks.home')} path={landingPagePath} />
            <MenuLink icon={faBowlHot} label={t('navLinks.orders')} path={ORDERS_PATH} />
            <MenuLink
              icon={faHeart}
              label={t('navLinks.savedRestaurants')}
              path={SAVED_CATERERS_PATH}
            />
            <MenuLink icon={faReceipt} label={t('navLinks.receipts')} path={RECEIPTS_PATH} />
          </div>

          {isLoggedIn && (
            <div className="flex flex-col items-start gap-4 pt-3">
              {canAccessEzRewards && (
                <MenuLink
                  chip={
                    balanceEnabled
                      ? t('user.points', {balance: formatPoints(ezRewardsAccount?.balance || 0)})
                      : undefined
                  }
                  icon={faGift}
                  label={t('navLinks.ezRewards')}
                  path={REWARDS_PATH}
                />
              )}

              {showRAFImprovements ? (
                <>
                  {canAccessEzRewards && (
                    <MenuLink
                      icon={faMessageHeart}
                      label={t('navLinks.referAFriend')}
                      path={REFER_A_FRIEND_PATH}
                      subText={earningsEnabled && t('user.referAFriendRewardsSubText')}
                    />
                  )}

                  <MenuLink
                    chipGreen
                    icon={faPencil}
                    label={t('navLinks.reviews')}
                    path={REVIEWS_PATH}
                  />
                </>
              ) : (
                <>
                  <MenuLink
                    chip={
                      earningsEnabled && t('user.plusPoints', {balance: POINTS_GIVEN_FOR_REVIEW})
                    }
                    chipGreen
                    icon={faPencil}
                    label={t('navLinks.reviews')}
                    path={REVIEWS_PATH}
                  />

                  {canAccessEzRewards && (
                    <MenuLink
                      chip={
                        earningsEnabled &&
                        t('user.plusPoints', {balance: POINTS_GIVEN_FOR_REFER_A_FRIEND})
                      }
                      chipGreen
                      icon={faMegaphone}
                      label={t('navLinks.referAFriend')}
                      path={REFER_A_FRIEND_PATH}
                    />
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex flex-col items-start gap-4 pt-3">
            <MenuLink icon={faCog} label={t('navLinks.settings')} path={MY_PROFILE_PATH} />
            {ordererConnectionsEnabled && shouldHideConnectionsFeatures && (
              <MenuLink
                chip="Beta"
                chipAgave
                icon={faUsers}
                label={t('navLinks.myTeam')}
                path={CONNECTIONS_PATH}
              />
            )}
            {ordererConnectionsEnabled && !shouldHideConnectionsFeatures && (
              <MenuLink
                chip={pendingConnectionsCount > 0 ? pendingConnectionsCount : undefined}
                chipGuava
                icon={faUsers}
                label={t('navLinks.connections')}
                path={CONNECTIONS_PATH}
              />
            )}
            <MenuLink
              icon={faLocationDot}
              label={t('navLinks.deliveryAddresses')}
              path={ADDRESSES_PATH}
            />
            <MenuLink
              icon={faCreditCard}
              label={t('navLinks.paymentMethods')}
              path={PAYMENT_METHODS_PATH}
            />

            {canAccessConcur && (
              <MenuLink
                icon={faMoneyCheckDollar}
                label={t('navLinks.expenseReporting')}
                path={EXPENSE_REPORTING_PATH}
              />
            )}
          </div>

          <div className="flex flex-col items-start gap-4 pt-3">
            {isMobileMenu && isAdmin && (
              <MenuLink icon={faLockOpen} label={t('navLinks.admin')} path={ADMIN_PATH} />
            )}

            {canAccessCorpPortal && Boolean(corpAccountUuid) && (
              <MenuLink
                icon={faBuildingUser}
                label={t('navLinks.enterpriseAdmin')}
                href={compilePath(CORP_DASHBOARD_PATH, {corpAccountUuid})}
                target="_blank"
              />
            )}

            {isMobileMenu && (
              <MenuLink
                href="https://ezcater.onelink.me/T4QA/7sd7rtk1"
                icon={
                  <div className="rounded-md bg-green-700 p-[3px]">
                    <EzcaterLogo
                      variant="symbol"
                      width={12}
                      height={12}
                      fillClassName="fill-green-300"
                    />
                  </div>
                }
                label={t('navLinks.downloadApp')}
                onClick={() => trackClick('app-download-cta-nav-mobile')}
                rel="noopener noreferrer"
                target="_blank"
              />
            )}

            {isLoggedIn && (
              <Button
                className="no-underline hover:underline"
                disabled={isSigningOut}
                onClick={() => signOut()}
                title={t('menus.navigatesTo', {label: t('navLinks.signOut')})}
                variant="buttonLink"
              >
                <div className="flex items-center gap-4">
                  <Icon className="text-md flex-shrink-0" icon={faRightFromBracket} />
                  <div className="text-left">{t('navLinks.signOutFullName', {fullName})}</div>
                </div>
              </Button>
            )}

            {!isMobileMenu && <AppDownloadCTA location="app-bar" />}
          </div>
        </>
      ) : (
        <div
          className={twJoin(
            'flex flex-col items-start gap-4 border-none',
            !isLoggedOutHomepage && 'mt-2',
          )}
        >
          <MenuLink
            icon={faArrowRightToBracket}
            label={t('navLinks.signIn')}
            path={SIGN_IN_PATH}
            queryParams={returnParams}
          />
          <MenuLink
            icon={faCirclePlus}
            label={t('navLinks.createAnAccount')}
            path={SIGN_UP_PATH}
            queryParams={returnParams}
          />
        </div>
      )}
    </div>
  );
};

export default UserMenu;
